/**
 * Player profile (public) — wireframe 28.
 *
 * Any logged-in user can open this screen for any other user via /u/{id}
 * — friendship is NOT required. The viewer sees the target's headline
 * stats, trivia honours, recent form, badges and head-to-head BEFORE
 * sending a friend request or a duel challenge.
 *
 * Privacy gates handled here:
 *   - Backend returns 404 when the target blocked the viewer; we fall
 *     back to the not-found state, identical to a genuinely missing
 *     user.
 *   - `is_private` profiles still render the identity block + "Add
 *     friend" so the viewer can request access, but stats / honours /
 *     form are zeroed out by the server.
 *   - `can_challenge` reflects the target's challenge_policy AND the
 *     viewer's relationship; we hide the Challenge button when false.
 *   - The owner sees the same screen with "Edit" + "Privacy" replacing
 *     "Add friend" + "Challenge".
 *
 * Wallet balances, bonus credit, naira winnings, KYC, contact info are
 * NEVER on the wire — the response schema doesn't carry those fields.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { icon, I } from '../components/icons.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatKobo } from '../wallet.js';

// Tier emoji + label table mirrors backend/app/services/leaderboard.py.
// Kept here so the profile chip renders the right glyph without a
// roundtrip to /leaderboard/me.
const TIERS = {
  bronze:   { label: 'Bronze',   emoji: '🥉' },
  silver:   { label: 'Silver',   emoji: '🥈' },
  gold:     { label: 'Gold',     emoji: '🥇' },
  platinum: { label: 'Platinum', emoji: '💠' },
  diamond:  { label: 'Diamond',  emoji: '💎' },
};

// Per-rank visual treatment for the honours card. 1 = gold, 2 = silver,
// 3 = bronze, 4-5 = top-5 blue, 6+ = neutral mute. The color is also
// used to tint the placement number badge so the trophy cabinet reads
// at a glance.
function honourStyle(rank) {
  if (rank === 1) return { color: '#F7C667', label: 'Winner' };
  if (rank === 2) return { color: '#C2C8D2', label: 'Runner-up' };
  if (rank === 3) return { color: '#D08C4A', label: '3rd place' };
  if (rank <= 5) return { color: '#7FB4FF', label: `Top ${rank}` };
  if (rank <= 10) return { color: '#7FB4FF', label: `Top 10` };
  return { color: 'var(--mute)', label: `#${rank}` };
}

function parsePlayerId(path) {
  const m = path.match(/^\/u\/([^/]+)$/);
  return m ? decodeURIComponent(m[1]) : null;
}

export async function renderPlayerProfile(root) {
  const targetId = parsePlayerId(window.location.pathname);
  if (!targetId) {
    navigate('/');
    return;
  }

  const meCached = auth.getMe();
  root.innerHTML = `
    <div class="appbar">
      <a href="javascript:void(0)" id="pp-back" class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Profile</div>
    </div>
    <div class="pad" id="pp-page">
      <div class="skel"></div>
      <div class="skel"></div>
      <div class="skel"></div>
    </div>
  `;

  document.getElementById('pp-back').addEventListener('click', (e) => {
    e.preventDefault();
    // Prefer the history stack so back returns to /friends or /ranks,
    // but if the page was opened from a deep link / cold start, fall
    // back to home.
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/');
    }
  });

  let profile;
  try {
    profile = await api(`/players/${encodeURIComponent(targetId)}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    if (err instanceof ApiError && err.status === 404) {
      document.getElementById('pp-page').innerHTML = `
        <div class="card center muted" style="padding:24px;">
          <div style="font-family:var(--font-display);font-size:14px;color:#fff;margin-bottom:6px;">Player not found</div>
          <div style="font-size:12px;">This account no longer exists or isn't available.</div>
        </div>
      `;
      return;
    }
    toast.fromError(err, 'Could not load profile.');
    document.getElementById('pp-page').innerHTML =
      `<div class="card" style="color:var(--pink-t)">${escape(err.message || 'Failed to load.')}</div>`;
    return;
  }

  const isSelf = profile.relationship === 'self';
  // Read `?tab=duels` / `?tab=honours` (or the honours default) so
  // "View all →" from Home and the Profile row link can deep-link
  // straight into the 1-v-1 tab without a second click.
  const initialTab = new URLSearchParams(window.location.search).get('tab') === 'duels'
    ? 'duels'
    : 'honours';
  document.getElementById('pp-page').innerHTML = renderBody(profile, isSelf, initialTab);
  wire(profile, isSelf, meCached);
  wireTabs(profile, isSelf);

  // Phase 8.12 — async duel record. Fetch separately so a slow or
  // failing call doesn't block the profile paint. Slot into the
  // existing "1-v-1 record" stat card as a secondary line so the
  // classic (live) 1-v-1 record stays where it was.
  if (!profile.is_private) {
    void loadAsyncDuelRecord(targetId);
  }
  // If the initial tab is "duels", eagerly load the duel-history
  // list — otherwise defer until the user taps the tab.
  if (initialTab === 'duels' && !profile.is_private) {
    void loadDuelsTab(targetId);
  }
}

// Tab strip on the profile — Trivia honours (default) vs 1-v-1.
// `wireTabs` swaps the visible pane on click and lazy-loads the
// duels-tab content on first activation.
function wireTabs(profile, _isSelf) {
  const buttons = document.querySelectorAll('.pp-tab-btn');
  if (!buttons.length) return;
  const honoursPane = document.getElementById('pp-tab-honours');
  const duelsPane = document.getElementById('pp-tab-duels');
  let duelsLoaded = false;
  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const tab = btn.dataset.ppTab;
      const active = tab === 'duels' ? duelsPane : honoursPane;
      const inactive = tab === 'duels' ? honoursPane : duelsPane;
      if (active) active.style.display = '';
      if (inactive) inactive.style.display = 'none';
      buttons.forEach((b) => {
        const isActive = b.dataset.ppTab === tab;
        b.style.color = isActive ? '#fff' : 'var(--mute)';
        b.style.borderBottomColor = isActive ? 'var(--teal-t)' : 'transparent';
      });
      // Update the URL so a refresh keeps the current tab (and so
      // deep-links to a tab stay stable).
      const url = new URL(window.location.href);
      if (tab === 'duels') url.searchParams.set('tab', 'duels');
      else url.searchParams.delete('tab');
      window.history.replaceState(null, '', url.toString());
      if (tab === 'duels' && !duelsLoaded) {
        duelsLoaded = true;
        void loadDuelsTab(profile.user_id);
      }
    });
  });
}

// Fetch + render the 1-v-1 tab content: summary card (W/L/T +
// total winnings for the profile owner) + list of finished duels.
async function loadDuelsTab(userId) {
  const pane = document.getElementById('pp-tab-duels');
  if (!pane) return;
  let payload;
  try {
    payload = await api(`/players/${encodeURIComponent(userId)}/duels?limit=50`);
  } catch (err) {
    pane.innerHTML = `<div class="card" style="color:var(--pink-t)">${escape(err.message || 'Could not load duel history.')}</div>`;
    return;
  }
  const items = payload?.items || [];
  if (!items.length) {
    pane.innerHTML = `
      <div class="card muted center" style="padding:20px;font-size:12px;">
        No finished duels yet.
      </div>
    `;
    return;
  }
  const summary = summarizeDuels(items, userId);
  pane.innerHTML = `
    ${renderDuelsSummary(summary)}
    <h3 class="sec">Finished duels</h3>
    ${items.map((d) => renderDuelHistoryRow(d, userId)).join('')}
  `;
}

function summarizeDuels(items, subjectId) {
  let wins = 0;
  let losses = 0;
  let ties = 0;
  let earningsKobo = 0;
  for (const d of items) {
    const isTie = d.status === 'completed' && !d.winner_id;
    if (isTie) {
      ties += 1;
    } else if (d.winner_id === subjectId) {
      wins += 1;
      earningsKobo += (d.money?.payout_kobo || 0);
    } else {
      losses += 1;
    }
  }
  return { wins, losses, ties, earningsKobo, total: items.length };
}

function renderDuelsSummary({ wins, losses, ties, earningsKobo, total }) {
  return `
    <div class="card" style="padding:14px;margin-top:9px;">
      <div class="row">
        <div class="stat">
          <div class="v" style="color:#5FE0BE;">${wins}</div>
          <div class="k">Wins</div>
        </div>
        <div class="stat">
          <div class="v" style="color:#FF8FB6;">${losses}</div>
          <div class="k">Losses</div>
        </div>
        <div class="stat">
          <div class="v">${ties}</div>
          <div class="k">Ties</div>
        </div>
        <div class="stat">
          <div class="v" style="color:var(--gold-t);">${escape(formatKobo(earningsKobo))}</div>
          <div class="k">Winnings</div>
        </div>
      </div>
      <div class="muted center" style="font-size:11px;margin-top:8px;">
        ${total} finished ${total === 1 ? 'duel' : 'duels'}
      </div>
    </div>
  `;
}

function renderDuelHistoryRow(duel, subjectId) {
  // Show the row from the profile-owner's perspective — "subject"
  // is the user whose profile we're viewing, not necessarily the
  // logged-in viewer.
  const runsById = new Map((duel.runs || []).map((r) => [r.user_id, r]));
  const subjectRun = runsById.get(subjectId) || null;
  const otherId = duel.creator?.id === subjectId ? duel.opponent?.id : duel.creator?.id;
  const otherRun = otherId ? runsById.get(otherId) : null;
  const otherPlayer = duel.creator?.id === subjectId ? duel.opponent : duel.creator;
  const myScore = subjectRun?.score ?? 0;
  const otherScore = otherRun?.score ?? 0;
  const isWin = duel.winner_id === subjectId;
  const isTie = duel.status === 'completed' && !duel.winner_id;
  const isForfeit = duel.status === 'forfeited';
  const payoutKobo = isWin ? (duel.money?.payout_kobo || 0) : 0;
  const payoutTag = payoutKobo > 0
    ? `<span style="display:inline-flex;align-items:center;padding:2px 7px;background:#3a2a14;border:1px solid rgba(229,193,0,0.45);border-radius:5px;font-family:var(--font-display);font-size:10.5px;color:#E5C100;font-weight:700;">
         +${escape(formatKobo(payoutKobo))}
       </span>`
    : '';
  const outcomeLabel = isTie
    ? 'Draw'
    : isWin
      ? (isForfeit ? 'Won by forfeit' : 'Won')
      : (isForfeit ? 'Forfeited' : 'Lost');
  const outcomeColor = isTie ? 'var(--mute)' : (isWin ? '#5FE0BE' : '#FF8FB6');
  const finishedAgo = duel.settled_at ? escape(fmtPastWhen(duel.settled_at)) : '';
  const stakeLabel = duel.stake_kobo > 0 ? formatKobo(duel.stake_kobo) : 'Free';
  return `
    <a href="/duel/${escape(duel.id)}" data-link class="card"
       style="padding:12px;text-decoration:none;display:block;">
      <div class="row" style="align-items:center;gap:10px;">
        <div class="grow" style="min-width:0;">
          <b style="font-size:13.5px;color:#fff;font-weight:600;">
            vs ${escape(otherPlayer?.username || 'Player')}
          </b>
          <div class="muted" style="font-size:11px;margin-top:2px;">
            ${escape(duel.category_name || 'Trivia')} · ${escape(stakeLabel)}
            ${finishedAgo ? ` · ${finishedAgo}` : ''}
          </div>
        </div>
        <div style="text-align:right;display:flex;flex-direction:column;gap:3px;align-items:flex-end;">
          <div style="font-family:var(--font-display);font-size:13px;color:#fff;font-weight:700;line-height:1;">
            ${myScore} – ${otherScore}
          </div>
          <div style="font-size:10.5px;color:${outcomeColor};font-weight:600;line-height:1;">
            ${outcomeLabel}
          </div>
          ${payoutTag}
        </div>
      </div>
    </a>
  `;
}

function fmtPastWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Math.max(0, Date.now() - d.getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-NG', { month: 'short', day: 'numeric' });
}

async function loadAsyncDuelRecord(userId) {
  let record;
  try {
    record = await api(`/players/${encodeURIComponent(userId)}/duel-record`);
  } catch {
    return;
  }
  const total = (record.wins || 0) + (record.losses || 0) + (record.ties || 0);
  if (total === 0) return;
  // Insert as a small suffix under the existing 1-v-1 record stat.
  const statBlock = document.querySelector('#pp-page .stat .v')?.closest('.stat');
  if (!statBlock) return;
  const line = document.createElement('div');
  line.style.cssText = 'font-size:10px;color:var(--mute);margin-top:2px';
  line.textContent = `Async · ${record.wins}W ${record.losses}L${record.ties ? ` ${record.ties}T` : ''}`;
  statBlock.appendChild(line);
}

function renderBody(p, isSelf, initialTab = 'honours') {
  const tier = TIERS[p.tier_key] || TIERS.bronze;
  return `
    ${heroBlock(p, tier)}
    ${ctaRow(p, isSelf)}
    ${p.is_private ? privateNotice() : statsSections(p, initialTab)}
    ${footerNote(p, isSelf)}
  `;
}

function heroBlock(p, tier) {
  const handle = p.username ? `@${escape(p.username)}` : escape(p.display_name || 'Player');
  const onlineDot = p.is_online
    ? '<span style="color:#5FE0BE;font-weight:500;">● online</span>'
    : `<span class="muted" style="font-weight:500;">${escape(lastSeenLabel(p.last_seen_at))}</span>`;
  const rankPill = (p.rank != null)
    ? `<span class="tag" style="background:rgba(255,255,255,0.16);color:#fff;">Rank #${p.rank.toLocaleString()}</span>`
    : '';
  const memberSince = memberSinceLabel(p.member_since);
  return `
    <div class="hero violet center" style="padding:20px;">
      <div class="av xl" style="margin:0 auto 9px;">${escape((p.display_name || 'P').slice(0, 1).toUpperCase())}</div>
      <div style="font-family:var(--font-display);font-size:20px;font-weight:700;color:#fff;">
        ${escape(p.display_name || 'Player')}
      </div>
      <div style="font-size:12px;font-weight:500;opacity:0.85;margin-top:1px;">
        ${handle} · ${onlineDot}
      </div>
      <div class="row" style="justify-content:center;gap:7px;margin-top:9px;flex-wrap:wrap;">
        <span class="tag gold">${escape(tier.label.toUpperCase())} ${escape(tier.emoji)}</span>
        ${rankPill}
        <span class="tag" style="background:rgba(255,255,255,0.16);color:#fff;">Since ${escape(memberSince)}</span>
      </div>
    </div>
  `;
}

function ctaRow(p, isSelf) {
  if (isSelf) {
    return `
      <div class="row" style="gap:9px;">
        <a href="/me" data-link class="btn ghost" style="flex:1;text-align:center;">Edit profile</a>
        <button type="button" class="btn ghost" id="pp-privacy" style="flex:1;">Privacy</button>
      </div>
    `;
  }
  const friendBtn = (() => {
    if (p.relationship === 'friend') {
      return '<span class="btn ghost" style="flex:1;text-align:center;cursor:default;">✓ Friends</span>';
    }
    if (p.relationship === 'pending_outgoing') {
      return '<span class="btn ghost" style="flex:1;text-align:center;cursor:default;">Request pending</span>';
    }
    if (p.relationship === 'pending_incoming') {
      return '<button type="button" class="btn teal" id="pp-accept" style="flex:1;">Accept request</button>';
    }
    if (p.relationship === 'blocked') {
      return '<button type="button" class="btn ghost" id="pp-unblock" style="flex:1;">Unblock</button>';
    }
    return '<button type="button" class="btn teal" id="pp-add" style="flex:1;">Add friend</button>';
  })();
  const challengeBtn = p.can_challenge
    ? '<button type="button" class="btn pink" id="pp-challenge" style="flex:1;">Challenge</button>'
    : '<span class="btn ghost" style="flex:1;text-align:center;cursor:default;opacity:0.55;">Challenges closed</span>';
  // Phase 8.12 — Message CTA. `can_message` is derived server-side
  // from viewer + target's chat_prefs + block state + friendship.
  // 'yes' → active link into compose mode.
  // 'friends_only' → disabled pill so the user can see the affordance
  //   exists but is currently gated by the recipient's preference.
  // 'no' → suppressed entirely (self / blocked / recipient='nobody').
  const messageBtn = (() => {
    const cm = p.can_message || 'no';
    if (cm === 'yes') {
      return `<a href="/messages/new/${encodeURIComponent(p.user_id)}" data-link class="btn teal" id="pp-message" style="flex:1;text-align:center;">Message</a>`;
    }
    if (cm === 'friends_only') {
      return '<span class="btn ghost" style="flex:1;text-align:center;cursor:default;opacity:0.55;" title="Only friends can message this user">Friends only</span>';
    }
    return '';
  })();
  return `
    <div class="row" style="gap:9px;">
      ${friendBtn}
      ${challengeBtn}
    </div>
    ${messageBtn ? `<div class="row" style="gap:9px;margin-top:9px;">${messageBtn}</div>` : ''}
  `;
}

function statsSections(p, initialTab = 'honours') {
  const winRatePct = (p.win_rate_bps / 100).toFixed(p.win_rate_bps % 100 === 0 ? 0 : 1);
  const accuracyPct = (p.accuracy_bps / 100).toFixed(p.accuracy_bps % 100 === 0 ? 0 : 1);
  const headlineStats = [
    [String(p.games_played), 'Games'],
    [`${winRatePct}%`, 'Win rate'],
    [String(p.best_streak), 'Best streak'],
    [`${accuracyPct}%`, 'Accuracy'],
  ];

  const honoursActive = initialTab !== 'duels';
  const duelsActive = initialTab === 'duels';

  return `
    <h3 class="sec">Stats</h3>
    <div class="row" style="gap:9px;flex-wrap:wrap;">
      ${headlineStats.map(([v, k]) => `
        <div class="card center" style="flex:1;min-width:80px;margin:0;padding:12px;">
          <div class="big" style="font-size:19px;color:#fff;font-weight:700;">${escape(v)}</div>
          <div class="k">${escape(k)}</div>
        </div>
      `).join('')}
    </div>
    <div class="card">
      <div class="row">
        <div class="stat">
          <div class="v" style="color:#5FE0BE;">${p.duel_wins}–${p.duel_losses + p.duel_draws}</div>
          <div class="k">1-v-1 record</div>
        </div>
        <div class="stat">
          <div class="v" style="color:var(--gold-t);">${p.live_trivia_wins}</div>
          <div class="k">Trivia wins</div>
        </div>
        <div class="stat">
          <div class="v">${p.best_score.toLocaleString()}</div>
          <div class="k">Best score</div>
        </div>
      </div>
    </div>

    <!-- Tab strip: Trivia honours (default) vs 1-v-1 history. -->
    <div class="row" style="gap:0;margin-top:14px;border-bottom:1px solid var(--border);">
      <button type="button" class="pp-tab-btn" data-pp-tab="honours"
              style="flex:1;padding:11px 9px;background:transparent;color:${honoursActive ? '#fff' : 'var(--mute)'};
                     border:0;border-bottom:2px solid ${honoursActive ? 'var(--teal-t)' : 'transparent'};
                     font-family:var(--font-display);font-size:12.5px;font-weight:600;letter-spacing:0.02em;cursor:pointer;">
        Trivia honours
      </button>
      <button type="button" class="pp-tab-btn" data-pp-tab="duels"
              style="flex:1;padding:11px 9px;background:transparent;color:${duelsActive ? '#fff' : 'var(--mute)'};
                     border:0;border-bottom:2px solid ${duelsActive ? 'var(--teal-t)' : 'transparent'};
                     font-family:var(--font-display);font-size:12.5px;font-weight:600;letter-spacing:0.02em;cursor:pointer;">
        1-v-1
      </button>
    </div>

    <div id="pp-tab-honours" style="${duelsActive ? 'display:none;' : ''}">
      ${honoursSection(p)}
      ${headToHeadNote(p)}
      ${recentFormSection(p)}
      ${topicsSection(p)}
      ${badgesSection(p)}
    </div>
    <div id="pp-tab-duels" style="${honoursActive ? 'display:none;' : ''}">
      <div class="muted center" style="padding:24px;font-size:12px;">
        Loading duel history…
      </div>
    </div>
  `;
}

function honoursSection(p) {
  if (!p.honours || p.honours.length === 0) {
    return `
      <h3 class="sec">${icon(I.trophy, { size: 15 })} Trivia honours</h3>
      <div class="card muted center" style="padding:14px;font-size:12px;">
        No top finishes yet — keep playing live trivias.
      </div>
    `;
  }
  return `
    <h3 class="sec">${icon(I.trophy, { size: 15 })} Trivia honours</h3>
    ${p.honours.map(honourRow).join('')}
  `;
}

function honourRow(h) {
  const s = honourStyle(h.rank);
  const date = h.finished_at ? new Date(h.finished_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
  const prize = (h.prize_kobo != null && h.prize_kobo > 0)
    ? `<div style="font-size:10.5px;color:var(--gold-t);font-weight:600;margin-top:2px;">${escape(formatKobo(h.prize_kobo))}</div>`
    : '';
  return `
    <div class="card" style="padding:11px;">
      <div class="row">
        <div class="ic" style="background:${s.color}22;color:${s.color};font-family:var(--font-display);font-weight:700;font-size:14px;">
          ${h.rank}
        </div>
        <div class="grow">
          <b style="font-size:13px;color:#fff;font-weight:600;">${escape(h.title)}</b>
          <div style="font-size:11px;color:var(--mute);font-weight:500;">Live trivia · ${escape(date)}</div>
          ${prize}
        </div>
        <span class="tag" style="background:${s.color}22;color:${s.color};">${escape(s.label)}</span>
      </div>
    </div>
  `;
}

function headToHeadNote(p) {
  if (!p.head_to_head) return '';
  const h = p.head_to_head;
  const totalLabel = h.draws > 0
    ? `${h.wins}–${h.losses}–${h.draws}`
    : `${h.wins}–${h.losses}`;
  return `
    <div class="note" style="border-color:#3A2D5F;background:#231C44;color:#C6BFEA;">
      <div class="row">
        <div class="ic" style="background:var(--violet-soft);color:#B7A6FF;">${icon(I.swords, { size: 16 })}</div>
        <div class="grow" style="margin-left:9px;">
          You vs ${escape(p.display_name || 'them')} — <b style="color:#fff;">${totalLabel}</b> in duels
        </div>
      </div>
    </div>
  `;
}

function recentFormSection(p) {
  if (!p.recent_form || p.recent_form.length === 0) return '';
  return `
    <h3 class="sec">Recent form</h3>
    <div class="row" style="gap:6px;flex-wrap:wrap;">
      ${p.recent_form.map(formChip).join('')}
    </div>
  `;
}

function formChip(r) {
  // Pink for losses + draws (anything not W) so the eye reads the row as
  // "wins lit up" at a glance.
  const bg = r === 'W' ? '#0F352A' : '#341428';
  const fg = r === 'W' ? '#5FE0BE' : '#FF9BC4';
  return `
    <span style="width:26px;height:26px;border-radius:8px;background:${bg};color:${fg};font-size:11px;font-weight:700;display:inline-flex;align-items:center;justify-content:center;font-family:var(--font-display);">
      ${escape(r)}
    </span>
  `;
}

function topicsSection(p) {
  if (!p.top_categories || p.top_categories.length === 0) return '';
  return `
    <h3 class="sec">Top categories</h3>
    <div>
      ${p.top_categories.map((t, i) => `<span class="pill ${i === 0 ? 'on' : ''}">${escape(t)}</span>`).join('')}
    </div>
  `;
}

function badgesSection(p) {
  if (!p.badges || p.badges.length === 0) return '';
  // The earned-badge slugs come from leaderboard.BADGES — we don't have
  // their emoji/label here without a second request, so we render them
  // as colored pills using the slug as a fallback label.
  const labelByKey = {
    first_drop: 'First Drop',
    triple_threat: 'Triple Threat',
    champion: 'Champion',
    hot_streak: 'Hot Streak',
    specialist: 'Specialist',
    centurion: 'Centurion',
  };
  const colorByKey = {
    first_drop: 'vio',
    triple_threat: 'gold',
    champion: 'gold',
    hot_streak: 'mag',
    specialist: 'teal',
    centurion: 'teal',
  };
  return `
    <h3 class="sec">Badges</h3>
    <div class="row" style="gap:6px;flex-wrap:wrap;">
      ${p.badges.map((b) => `<span class="tag ${colorByKey[b] || 'vio'}">${escape(labelByKey[b] || b)}</span>`).join('')}
    </div>
  `;
}

function privateNotice() {
  return `
    <div class="card muted center" style="padding:18px;margin-top:8px;">
      <div style="font-size:22px;margin-bottom:6px;">🔒</div>
      <div style="font-family:var(--font-display);font-size:13px;color:#fff;margin-bottom:4px;">
        This profile is private
      </div>
      <div style="font-size:12px;">
        Send a friend request to see their stats, honours and form.
      </div>
    </div>
  `;
}

function footerNote(p, isSelf) {
  if (isSelf) {
    return `
      <div class="note" style="border-color:var(--border);background:var(--surf);color:var(--mute);margin-top:14px;">
        This is how your public profile looks to others. Wallet, bonus and winnings are private.
      </div>
    `;
  }
  return `
    <div class="note" style="border-color:var(--border);background:var(--surf);color:var(--mute);margin-top:14px;">
      Winnings &amp; wallet are private. This is ${escape(p.display_name || 'their')} public profile — anyone can view it before adding or challenging.
    </div>
  `;
}

function memberSinceLabel(iso) {
  if (!iso) return 'recently';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  } catch {
    return 'recently';
  }
}

function lastSeenLabel(iso) {
  if (!iso) return 'offline';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return 'just now';
    const m = Math.floor(ms / 60_000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return 'offline';
  } catch {
    return 'offline';
  }
}

// ---- interactions ---------------------------------------------------------

function wire(profile, isSelf, _me) {
  const addBtn = document.getElementById('pp-add');
  if (addBtn) {
    addBtn.addEventListener('click', async () => {
      addBtn.disabled = true;
      addBtn.textContent = 'Sending…';
      try {
        await api('/friends/requests', { method: 'POST', body: { to_user_id: profile.user_id } });
        toast.success('Request sent');
        addBtn.textContent = 'Request pending';
      } catch (err) {
        addBtn.disabled = false;
        addBtn.textContent = 'Add friend';
        toast.error(err.message || 'Could not send request.');
      }
    });
  }

  const acceptBtn = document.getElementById('pp-accept');
  if (acceptBtn) {
    acceptBtn.addEventListener('click', async () => {
      // Have to find the request id first — /friends/requests returns
      // incoming pending rows.
      try {
        const reqs = await api('/friends/requests');
        const match = reqs.find((r) => r.user.id === profile.user_id);
        if (!match) {
          toast.error('Request no longer pending.');
          return;
        }
        await api(`/friends/requests/${match.id}/accept`, { method: 'POST' });
        toast.success('You are now friends');
        // Re-render so the relationship pill updates.
        renderPlayerProfile(document.getElementById('route-shell'));
      } catch (err) {
        toast.error(err.message || 'Could not accept.');
      }
    });
  }

  const unblockBtn = document.getElementById('pp-unblock');
  if (unblockBtn) {
    unblockBtn.addEventListener('click', async () => {
      try {
        await api(`/friends/blocks/${encodeURIComponent(profile.user_id)}`, { method: 'DELETE' });
        toast.success('Unblocked');
        renderPlayerProfile(document.getElementById('route-shell'));
      } catch (err) {
        toast.error(err.message || 'Could not unblock.');
      }
    });
  }

  const chBtn = document.getElementById('pp-challenge');
  if (chBtn) {
    chBtn.addEventListener('click', () => openChallengeSheet(profile));
  }

  const privacyBtn = document.getElementById('pp-privacy');
  if (privacyBtn) {
    privacyBtn.addEventListener('click', () => openPrivacySheet());
  }
}

// ---- challenge sheet (shared shape with /friends) -------------------------

const STAKES = [
  { kobo: 0, label: 'Free' },
  { kobo: 10_000, label: '₦100' },
  { kobo: 20_000, label: '₦200' },
  { kobo: 50_000, label: '₦500' },
];

async function openChallengeSheet(profile) {
  let categories = [];
  try {
    categories = await api('/game/categories');
  } catch (err) {
    toast.error(err.message || 'No categories.');
    return;
  }
  if (!categories.length) {
    toast.error('No categories available.');
    return;
  }
  let selectedCat = categories[0];
  let selectedDiff = 'medium';
  let selectedStake = 0;

  const overlay = document.createElement('div');
  overlay.className = 'sheet-overlay';
  overlay.innerHTML = `
    <div class="sheet">
      <div class="row" style="margin-bottom:8px;">
        <div class="grow">
          <b style="font-family:var(--font-display);font-size:15px;color:#fff;">
            Challenge ${escape(profile.display_name || 'this player')}
          </b>
          <div class="muted" style="font-size:11px;">10-question duel · winner takes the pool</div>
        </div>
        <button type="button" class="btn ghost" id="ppc-close" style="margin:0;width:auto;padding:7px 10px;font-size:18px;line-height:1;">×</button>
      </div>
      <h3 class="sec" style="margin-top:4px;">Category</h3>
      <div id="ppc-cats" class="topic-grid">
        ${categories.map((c) => `<span class="pill" data-cat="${escape(c.id)}">${escape(c.name)}</span>`).join('')}
      </div>
      <h3 class="sec">Difficulty</h3>
      <div id="ppc-diffs" class="topic-grid">
        ${['easy', 'medium', 'hard'].map((d) => `<span class="pill" data-diff="${d}">${d}</span>`).join('')}
      </div>
      <h3 class="sec">Stake</h3>
      <div id="ppc-stakes" class="topic-grid">
        ${STAKES.map((s) => `<span class="pill" data-stake="${s.kobo}">${s.label}</span>`).join('')}
      </div>
      <button type="button" class="btn gold" id="ppc-send" style="margin-top:14px;">Send challenge</button>
    </div>
  `;
  document.body.appendChild(overlay);
  injectSheetStyles();
  const close = () => overlay.remove();
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('#ppc-close').addEventListener('click', close);

  function paint() {
    overlay.querySelectorAll('#ppc-cats .pill').forEach((p) => p.classList.toggle('on', p.dataset.cat === selectedCat.id));
    overlay.querySelectorAll('#ppc-diffs .pill').forEach((p) => p.classList.toggle('on', p.dataset.diff === selectedDiff));
    overlay.querySelectorAll('#ppc-stakes .pill').forEach((p) => p.classList.toggle('on', parseInt(p.dataset.stake, 10) === selectedStake));
  }
  paint();
  overlay.querySelector('#ppc-cats').addEventListener('click', (e) => {
    const p = e.target.closest('.pill'); if (!p) return;
    selectedCat = categories.find((c) => c.id === p.dataset.cat) || selectedCat;
    paint();
  });
  overlay.querySelector('#ppc-diffs').addEventListener('click', (e) => {
    const p = e.target.closest('.pill'); if (!p) return;
    selectedDiff = p.dataset.diff;
    paint();
  });
  overlay.querySelector('#ppc-stakes').addEventListener('click', (e) => {
    const p = e.target.closest('.pill'); if (!p) return;
    selectedStake = parseInt(p.dataset.stake, 10) || 0;
    paint();
  });

  overlay.querySelector('#ppc-send').addEventListener('click', async () => {
    const send = overlay.querySelector('#ppc-send');
    send.disabled = true;
    send.textContent = 'Sending…';
    try {
      await api('/challenges', {
        method: 'POST',
        body: {
          to_user_id: profile.user_id,
          category_id: selectedCat.id,
          difficulty: selectedDiff,
          stake_kobo: selectedStake,
          question_count: 10,
        },
      });
      toast.success('Challenge sent');
      close();
      navigate('/friends?tab=challenges');
    } catch (err) {
      send.disabled = false;
      send.textContent = 'Send challenge';
      if (err instanceof ApiError && err.code === 'challenges_closed') {
        toast.error("This player isn't accepting challenges right now.");
      } else if (err instanceof ApiError && err.code === 'friends_only') {
        toast.error('Only their friends can challenge them — send a friend request first.');
      } else {
        toast.error(err.message || 'Could not send.');
      }
    }
  });
}

// ---- privacy sheet (self only) -------------------------------------------

async function openPrivacySheet() {
  let current;
  try {
    current = await api('/players/me/privacy');
  } catch (err) {
    toast.error(err.message || 'Could not load privacy.');
    return;
  }

  const overlay = document.createElement('div');
  overlay.className = 'sheet-overlay';
  overlay.innerHTML = `
    <div class="sheet">
      <div class="row" style="margin-bottom:8px;">
        <div class="grow">
          <b style="font-family:var(--font-display);font-size:15px;color:#fff;">Profile privacy</b>
          <div class="muted" style="font-size:11px;">Control who can see your stats and challenge you.</div>
        </div>
        <button type="button" class="btn ghost" id="ppp-close" style="margin:0;width:auto;padding:7px 10px;font-size:18px;line-height:1;">×</button>
      </div>

      <h3 class="sec" style="margin-top:4px;">Profile visibility</h3>
      <div class="topic-grid" id="ppp-vis">
        ${['public', 'private'].map((v) => `<span class="pill" data-vis="${v}">${v[0].toUpperCase() + v.slice(1)}</span>`).join('')}
      </div>

      <h3 class="sec">Hide winnings on honours</h3>
      <div class="topic-grid" id="ppp-hide">
        <span class="pill" data-hide="false">Show prize amounts</span>
        <span class="pill" data-hide="true">Hide prize amounts</span>
      </div>

      <h3 class="sec">Who can challenge me</h3>
      <div class="topic-grid" id="ppp-policy">
        ${[
          ['everyone', 'Everyone'],
          ['friends', 'Friends only'],
          ['nobody', 'Nobody'],
        ].map(([v, l]) => `<span class="pill" data-policy="${v}">${l}</span>`).join('')}
      </div>

      <button type="button" class="btn teal" id="ppp-save" style="margin-top:14px;">Save</button>
    </div>
  `;
  document.body.appendChild(overlay);
  injectSheetStyles();
  const close = () => overlay.remove();
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('#ppp-close').addEventListener('click', close);

  let visibility = current.profile_visibility;
  let hide = current.hide_winnings;
  let policy = current.challenge_policy;

  function paint() {
    overlay.querySelectorAll('#ppp-vis .pill').forEach((p) => p.classList.toggle('on', p.dataset.vis === visibility));
    overlay.querySelectorAll('#ppp-hide .pill').forEach((p) => p.classList.toggle('on', String(hide) === p.dataset.hide));
    overlay.querySelectorAll('#ppp-policy .pill').forEach((p) => p.classList.toggle('on', p.dataset.policy === policy));
  }
  paint();

  overlay.querySelector('#ppp-vis').addEventListener('click', (e) => {
    const p = e.target.closest('.pill'); if (!p) return;
    visibility = p.dataset.vis;
    paint();
  });
  overlay.querySelector('#ppp-hide').addEventListener('click', (e) => {
    const p = e.target.closest('.pill'); if (!p) return;
    hide = p.dataset.hide === 'true';
    paint();
  });
  overlay.querySelector('#ppp-policy').addEventListener('click', (e) => {
    const p = e.target.closest('.pill'); if (!p) return;
    policy = p.dataset.policy;
    paint();
  });

  overlay.querySelector('#ppp-save').addEventListener('click', async () => {
    const save = overlay.querySelector('#ppp-save');
    save.disabled = true;
    save.textContent = 'Saving…';
    try {
      await api('/players/me/privacy', {
        method: 'PUT',
        body: {
          profile_visibility: visibility,
          hide_winnings: hide,
          challenge_policy: policy,
        },
      });
      toast.success('Privacy updated');
      close();
    } catch (err) {
      save.disabled = false;
      save.textContent = 'Save';
      toast.error(err.message || 'Could not save.');
    }
  });
}

function injectSheetStyles() {
  if (document.getElementById('player-profile-sheet-styles')) return;
  const s = document.createElement('style');
  s.id = 'player-profile-sheet-styles';
  s.textContent = `
    .sheet-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.5); z-index:60; display:flex; align-items:flex-end; justify-content:center; }
    .sheet { background:var(--surf-2); border-top:1px solid var(--violet); border-radius:14px 14px 0 0; padding:18px; width:100%; max-width:480px; max-height:80vh; overflow:auto; }
  `;
  document.head.appendChild(s);
}
